// A visual's colours — ONE answer for every chart.
//
// The catalog option `themeColor` names a colour theme (visualPalettes.json).
// 'Default' (and a blank value, which every visual saved before 'Default' was
// the catalog default carries) is NOT a palette of its own: it is the
// APPLICATION's theme colour (application.theme_color), exactly as set — not
// the platform's accent, and not the darkened copy a screen frame makes so
// TEXT in that colour stays readable (bars are fills) — followed by the
// categorical palette for further series. Which CSS variable carries it on
// each surface is data (visualPalettes.json `accentVars`). Before this, a blank theme fell
// through the vendored charts to a hard-coded "Reds" scheme, so every new
// chart was drawn red.
//
// The vendored chart classes ask through ONE seam, `getThemeColorPalette()`,
// which delegates to the resolver `installVisualColors` puts on BaseVisual.
// ADV_FORMAT's `theme` key (chartAdvFormat.ts) resolves through the same
// `themePalette`, so "Alert" means the same colours on a whole chart and on
// the points a condition picks out.

import PALETTES from './visualPalettes.json'
import { expandHex, isHexColor, parseColor, toHex, type RGB } from './color'

export interface VisualTheme {
  id: string
  label: string
  colors?: string[]
}

export const VISUAL_THEMES: VisualTheme[] = PALETTES.themes as VisualTheme[]
const DEFAULT_ID = String(PALETTES.default || 'default').toLowerCase()
const ACCENT_VARS: string[] = PALETTES.accentVars as string[]
const FALLBACK_ACCENT: string = PALETTES.fallbackAccent
const CATEGORICAL: string[] = PALETTES.categorical as string[]
const MIN_DISTANCE: number = Number(PALETTES.minDistance) || 60

/** True for 'Default', blank and absent — the surface's own theme colour. */
export function isDefaultTheme(name: unknown): boolean {
  const n = String(name ?? '').trim().toLowerCase()
  return !n || n === DEFAULT_ID
}

/** The named theme (case-insensitive id or label), or null. */
export function findTheme(name: unknown): VisualTheme | null {
  const n = String(name ?? '').trim().toLowerCase()
  if (!n) return null
  return VISUAL_THEMES.find((t) => t.id.toLowerCase() === n || t.label.toLowerCase() === n) ?? null
}

/** The application's theme colour as published behind `el`: the first
 *  accent variable (visualPalettes.json `accentVars`) holding a colour. */
export function themeAccent(el: Element | null | undefined): string {
  if (typeof window === 'undefined' || !el || typeof getComputedStyle !== 'function') return FALLBACK_ACCENT
  try {
    const cs = getComputedStyle(el)
    for (const name of ACCENT_VARS) {
      const raw = cs.getPropertyValue(name).trim()
      const rgb = raw ? parseColor(raw) : null
      if (rgb) return toHex(rgb)
    }
  } catch (err) {
    console.warn('[visual-engine] theme colour could not be read; using the fallback accent', err)
  }
  return FALLBACK_ACCENT
}

function distance(a: RGB, b: RGB): number {
  return Math.sqrt((a[0] - b[0]) ** 2 + (a[1] - b[1]) ** 2 + (a[2] - b[2]) ** 2)
}

/** The Default palette for a theme colour: that colour first, then the
 *  categorical palette minus the entries that would read as the same hue. */
export function defaultPalette(accent: string): string[] {
  const a = parseColor(accent) ?? parseColor(FALLBACK_ACCENT)!
  const first = toHex(a)
  const rest = CATEGORICAL.filter((c) => {
    const rgb = parseColor(c)
    return rgb ? distance(rgb, a) >= MIN_DISTANCE : false
  })
  return [first, ...rest]
}

/**
 * The colours a theme name stands for, on the surface `el`:
 *  - Default / blank → the surface's theme colour + categorical;
 *  - a theme id or label → its colours;
 *  - a '#hex' → that one colour;
 *  - anything else → null (unknown — the caller keeps its own colour).
 */
export function themePalette(name: unknown, el?: Element | null, accent?: string | null): string[] | null {
  // `accent`: the application's colour, when the caller knows it (a designer
  // reads it from the project) — it wins over what the surface publishes.
  const appAccent = () => (accent && parseColor(accent) ? toHex(parseColor(accent)!) : themeAccent(el ?? null))
  if (isDefaultTheme(name)) return defaultPalette(appAccent())
  const t = findTheme(name)
  if (t) return t.colors && t.colors.length ? t.colors.slice() : defaultPalette(appAccent())
  const s = String(name ?? '').trim()
  if (isHexColor(s)) return [expandHex(s)]
  return null
}

interface ChartForColours {
  containerElement?: Element | null
  getOption?: (name: string) => unknown
}

interface BaseVisualCtor {
  colorResolver?: { palette: (name: unknown, chart: ChartForColours) => string[] | null }
}

/** Give the vendored BaseVisual its colour resolver (idempotent). */
export function installVisualColors(BaseVisual: unknown): void {
  const ctor = BaseVisual as BaseVisualCtor | null
  if (!ctor || (typeof ctor !== 'function' && typeof ctor !== 'object')) return
  if (ctor.colorResolver) return
  ctor.colorResolver = {
    palette: (name, chart) => {
      try {
        return themePalette(name, chart?.containerElement ?? null)
      } catch (err) {
        console.warn('[visual-engine] colour theme not resolved:', name, err)
        return null
      }
    },
  }
}
